import moment from "moment";
import "moment/locale/es";

const normalize = (value = "") => `${value}`
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const percentile = (sortedValues, position) => {
  if (!sortedValues.length) return 0;
  const index = (sortedValues.length - 1) * position;
  const lower = Math.floor(index);
  const fraction = index - lower;
  return sortedValues[lower + 1] !== undefined
    ? sortedValues[lower] + fraction * (sortedValues[lower + 1] - sortedValues[lower])
    : sortedValues[lower];
};

const getMeasureType = (measure) => {
  const unit = normalize(measure?.measure?.medida?.vc_abreviatura).replace(/\s/g, "");
  const description = normalize([
    measure?.name,
    measure?.measure?.vc_fieldName,
    measure?.measure?.vc_tag,
    measure?.measure?.vc_nombre,
    measure?.measure?.vc_descripcion,
    measure?.measure?.medida?.vc_nombre,
    measure?.measure?.medida?.vc_descripcion,
  ].filter(Boolean).join(" "));

  if (["kwh", "wh", "mwh"].includes(unit) || /energia|consumo/.test(description)) return "energy";
  if (["hz", "khz"].includes(unit) || /frecuencia/.test(description)) return "frequency";
  if (["a", "ma", "ka"].includes(unit) || /corriente|amper/.test(description)) return "current";
  if (["v", "mv", "kv"].includes(unit) || /voltaje|tension|volts/.test(description)) return "voltage";
  if (["fp", "pf", "cosphi", "cosφ"].includes(unit) || /factor.*potencia|power.*factor/.test(description)) return "powerFactor";
  if (["w", "kw", "mw", "va", "kva", "var", "kvar"].includes(unit) || /potencia|demanda/.test(description)) return "power";
  return "other";
};

const formatValue = (value, unit) => `${Number(value).toFixed(2)}${unit ? ` ${unit}` : ""}`;

const finiteOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getRelationName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.vc_nombre || value.nombre || value.name || value.vc_descripcion || "";
};

const parseJson = (value) => {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getDeviceLocation = (selected = {}) => {
  const device = selected ?? {};
  const source = parseJson(device.json_ubicacion) || device.ubicacion || device.location || {};
  const coordinates = source?.geometry?.coordinates || source?.coordinates;
  const latitude = finiteOrNull(
    device.vc_lat ?? device.latitude ?? device.lat ?? source.lat ?? source.latitude ?? source.center?.lat ??
    (Array.isArray(coordinates) ? coordinates[1] : null)
  );
  const longitude = finiteOrNull(
    device.vc_lon ?? device.longitude ?? device.lng ?? source.lng ?? source.lon ?? source.longitude ?? source.center?.lng ??
    (Array.isArray(coordinates) ? coordinates[0] : null)
  );
  if (latitude === null || longitude === null) return null;
  return {
    latitude,
    longitude,
    label: source.formattedAddress || source.address || source.vc_nombre || device.vc_ubicacion || "Ubicación registrada",
  };
};

const getDeviceType = (selected = {}) => {
  const device = selected ?? {};
  return getRelationName(
    device.tipo || device.tipoDispositivo || device.type || device.deviceType || device.dispositivo?.tipo
  ) || device.vc_tipo || device.vc_tipoDispositivo || device.id_tipo || "No especificado";
};

const matchesMeasure = (alarm, series) => {
  const measureIds = [
    series?.measure?.id,
    series?.measure?.id_tipoMedida,
    series?.measure?.medida?.id,
  ].filter(Boolean);
  return measureIds.includes(alarm?.id_tipoMedida) ||
    measureIds.includes(alarm?.medida?.id) ||
    normalize(alarm?.medida?.vc_nombre) === normalize(series?.name);
};

const buildAlertConfiguration = (alarm, measures) => {
  const matchedMeasure = measures.find((measure) => matchesMeasure(alarm, measure));
  const min = finiteOrNull(alarm?.nu_min);
  const max = finiteOrNull(alarm?.nu_max);
  return {
    id: alarm?.id || `${alarm?.id_tipoMedida || "alert"}-${min}-${max}`,
    measureId: alarm?.id_tipoMedida || alarm?.medida?.id || null,
    measureName: matchedMeasure?.name || alarm?.medida?.vc_nombre || alarm?.medida?.medida?.vc_nombre || "Medición no identificada",
    type: getRelationName(alarm?.tipoAlerta || alarm?.tipo_alerta) || (alarm?.id_tipoAlerta ? `Tipo ${alarm.id_tipoAlerta}` : "Sin tipo asignado"),
    min,
    max,
    toleranceSeconds: finiteOrNull(alarm?.nu_toleranciaTiempo),
    toleranceEvents: finiteOrNull(alarm?.nu_toleranciaEventos),
  };
};

const buildEventConfiguration = (event, index) => ({
  id: event?.id || `event-${index}`,
  description: event?.vc_descripcion || event?.descripcion || `Evento ${index + 1}`,
  type: getRelationName(event?.tipo || event?.tipoEvento || event?.type) || (event?.id_tipo ? `Tipo ${event.id_tipo}` : "Sin tipo asignado"),
  observations: event?.vc_observaciones || event?.observaciones || "Sin observaciones",
  startDate: event?.dt_inicio ? moment(event.dt_inicio).locale("es-MX").format("DD MMM YYYY") : "Sin fecha de inicio",
  endDate: event?.dt_fin ? moment(event.dt_fin).locale("es-MX").format("DD MMM YYYY") : "Sin fecha de término",
  startTime: event?.tm_inicio || "Sin horario",
  frequency: finiteOrNull(event?.nu_frecuencia),
  frequencyUnit: getRelationName(event?.unidadFrecuencia) || event?.vc_unidadFrecuencia || "",
});

const getTechnicalAnalysis = ({ type, stats, unit, configuredMin, configuredMax }) => {
  const rangeText = formatValue(stats.range, unit);
  const variationText = `${stats.coefficientVariation.toFixed(2)}%`;
  const anomalyText = stats.anomalyCount
    ? `Se identificaron ${stats.anomalyCount} muestras estadísticamente atípicas (fuera de ±2 desviaciones estándar).`
    : "No se identificaron muestras atípicas con el criterio de ±2 desviaciones estándar.";
  const limitText = stats.outOfRangeCount
    ? `${stats.outOfRangeCount} registros quedaron fuera de los límites configurados.`
    : (Number.isFinite(configuredMin) || Number.isFinite(configuredMax))
      ? "Todos los registros permanecieron dentro de los límites configurados."
      : "No hay límites nominales configurados; la lectura es estadística y no determina cumplimiento normativo.";

  switch (type) {
    case "voltage":
      return `Análisis de tensión: la amplitud observada fue ${rangeText} y la variación relativa ${variationText}. ${anomalyText} ${limitText}`;
    case "current":
      return `Análisis de carga: la relación pico/promedio fue ${stats.peakToAverage.toFixed(2)} y la variación relativa ${variationText}. ${anomalyText} ${limitText}`;
    case "frequency":
      return `Análisis de frecuencia: la dispersión máxima del periodo fue ${rangeText}, con desviación estándar de ${formatValue(stats.standardDeviation, unit)}. ${anomalyText} ${limitText}`;
    case "power":
      return `Análisis de demanda: el factor de carga (promedio/pico) fue ${stats.loadFactor.toFixed(2)}%. El pico registrado fue ${formatValue(stats.max, unit)}. ${anomalyText} ${limitText}`;
    case "energy":
      return `Análisis de energía: el cambio neto entre la primera y la última lectura fue ${formatValue(stats.netChange, unit)}. ${anomalyText} ${limitText}`;
    case "powerFactor":
      return `Análisis de factor de potencia: el promedio fue ${stats.average.toFixed(3)} y el valor más bajo ${stats.min.toFixed(3)}. Para evaluar penalizaciones o conformidad deben configurarse los límites contractuales. ${limitText}`;
    default:
      return `Análisis estadístico: el rango observado fue ${rangeText}, la variación relativa ${variationText} y la tendencia del periodo ${stats.trendPercent >= 0 ? "+" : ""}${stats.trendPercent.toFixed(2)}%. ${anomalyText} ${limitText}`;
  }
};

const getPhaseInsight = (measures, type, label) => {
  const phaseMeasures = measures.filter((measure) => {
    const identity = normalize([
      measure?.name,
      measure?.measure?.vc_fieldName,
      measure?.measure?.vc_tag,
      measure?.measure?.vc_descripcion,
    ].filter(Boolean).join(" "));
    return measure.type === type && /fase|phase|(^|[^a-z])l[123]([^a-z]|$)/.test(identity);
  });
  if (phaseMeasures.length < 2) return null;
  const averages = phaseMeasures.map((measure) => measure.stats.average);
  const groupAverage = averages.reduce((sum, value) => sum + value, 0) / averages.length;
  if (!groupAverage) return null;
  const unbalance = Math.max(...averages.map((value) => Math.abs(value - groupAverage))) / Math.abs(groupAverage) * 100;
  return {
    type,
    title: `Balance de ${label}`,
    value: `${unbalance.toFixed(2)}%`,
    severity: unbalance > 5 ? "warning" : "ok",
    text: `La máxima desviación entre promedios fue ${unbalance.toFixed(2)}%. Es un indicador comparativo de las fases disponibles y no sustituye un estudio de componentes simétricas.`,
  };
};

export const buildIntelligentReport = ({
  series = [],
  user = {},
  selected = {},
  range = [],
  alarms = [],
  eventConfigurations = [],
}) => {
  const generatedAt = moment();
  let header = `\nFecha: ${generatedAt.locale("es-MX").format("DD MMMM YYYY")}\n`;
  header += `Hora: ${generatedAt.locale("es-MX").format("HH:mm:ss")}\n`;
  header += `Supervisor: ${user?.vc_nombre || " "} ${user?.vc_paterno || " "} ${user?.vc_materno || " "}\n\n`;
  header += "Se realiza análisis automático de los registros del dispositivo seleccionado:\n\n";
  header += `Dispositivo: ${selected?.vc_nombre || "No identificado"}.\nID: ${selected?.id_dispositivo || selected?.id || "No identificado"}\n\n`;

  const measures = (Array.isArray(series) ? series : []).flatMap((measure) => {
    const data = (Array.isArray(measure?.data) ? measure.data : []).filter(
      (register) => Number.isFinite(Number(register?.[1]))
    );
    if (!data.length) return [];

    const numericValues = data.map((register) => Number(register[1]));
    const sortedValues = [...numericValues].sort((a, b) => a - b);
    const minIndex = numericValues.indexOf(Math.min(...numericValues));
    const maxIndex = numericValues.indexOf(Math.max(...numericValues));
    const min = data[minIndex];
    const max = data[maxIndex];
    const average = numericValues.reduce((sum, current) => sum + current, 0) / numericValues.length;
    const variance = numericValues.reduce((sum, current) => sum + ((current - average) ** 2), 0) / numericValues.length;
    const standardDeviation = Math.sqrt(variance);
    const startDate = moment(data[0]?.[2] || data[0]?.[0]);
    const endRegister = data[data.length - 1];
    const endDate = moment(endRegister?.[2] || endRegister?.[0]);
    const startDateString = range?.[0]
      ? moment(range[0]).locale("es-MX").format("DD MMMM YYYY")
      : startDate.locale("es-MX").format("DD MMMM YYYY");
    const endDateString = range?.[1]
      ? moment(range[1]).locale("es-MX").format("DD MMMM YYYY")
      : endDate.locale("es-MX").format("DD MMMM YYYY");
    const lapseHours = Math.abs(endDate.diff(startDate, "hours"));
    const lapseString = lapseHours < 24 ? `${lapseHours} horas` : `${Math.round(lapseHours / 24)} días`;
    const unit = measure?.measure?.medida?.vc_abreviatura || "";
    const suffix = unit.trim() ? ` ${unit}` : "";
    const relatedAlarms = (Array.isArray(alarms) ? alarms : []).filter((alarm) => matchesMeasure(alarm, measure));
    const alarmMin = relatedAlarms.map((alarm) => finiteOrNull(alarm?.nu_min)).find((value) => value !== null);
    const alarmMax = relatedAlarms.map((alarm) => finiteOrNull(alarm?.nu_max)).find((value) => value !== null);
    const measureMin = finiteOrNull(measure?.measure?.nu_min);
    const measureMax = finiteOrNull(measure?.measure?.nu_max);
    const configuredMin = alarmMin ?? measureMin;
    const configuredMax = alarmMax ?? measureMax;
    const hasConfiguredMin = configuredMin !== null;
    const hasConfiguredMax = configuredMax !== null;
    const anomalyCount = standardDeviation === 0 ? 0 : numericValues.filter((current) => Math.abs(current - average) > 2 * standardDeviation).length;
    const outOfRangeCount = numericValues.filter((current) =>
      (hasConfiguredMin && current < configuredMin) || (hasConfiguredMax && current > configuredMax)
    ).length;
    const belowCount = hasConfiguredMin ? numericValues.filter((current) => current < configuredMin).length : 0;
    const aboveCount = hasConfiguredMax ? numericValues.filter((current) => current > configuredMax).length : 0;
    const withinCount = numericValues.length - belowCount - aboveCount;
    const first = numericValues[0];
    const latest = numericValues[numericValues.length - 1];
    const trendPercent = first === 0 ? 0 : ((latest - first) / Math.abs(first)) * 100;
    const coefficientVariation = average === 0 ? 0 : (standardDeviation / Math.abs(average)) * 100;
    const stats = {
      count: data.length,
      min: Number(min[1]),
      max: Number(max[1]),
      average,
      median: percentile(sortedValues, 0.5),
      percentile95: percentile(sortedValues, 0.95),
      latest,
      first,
      range: Number(max[1]) - Number(min[1]),
      standardDeviation,
      coefficientVariation,
      trendPercent,
      netChange: latest - first,
      anomalyCount,
      outOfRangeCount,
      peakToAverage: average === 0 ? 0 : Math.abs(Number(max[1]) / average),
      loadFactor: Number(max[1]) === 0 ? 0 : Math.abs(average / Number(max[1])) * 100,
    };
    const type = getMeasureType(measure);
    const severity = outOfRangeCount > 0 || anomalyCount / data.length > 0.05
      ? "warning"
      : coefficientVariation > 15
        ? "attention"
        : "ok";
    const analysis = `Se encontraron ${data.length} registros de ${measure.name} entre el ${startDateString} y el ${endDateString}, en un periodo de ${lapseString}.` +
      `\nEl valor mínimo fue ${Number(min[1]).toFixed(2)}${suffix}, registrado el ${moment(min[2] || min[0]).locale("es-MX").format("DD MMMM YYYY")}; ` +
      `el máximo fue ${Number(max[1]).toFixed(2)}${suffix}, registrado el ${moment(max[2] || max[0]).locale("es-MX").format("DD MMMM YYYY")}. ` +
      `El promedio fue ${average.toFixed(2)}${suffix} y la variabilidad ${Math.abs(Number(max[1]) - Number(min[1])).toFixed(2)}${suffix}.`;
    const technicalAnalysis = getTechnicalAnalysis({
      type,
      stats,
      unit,
      configuredMin: hasConfiguredMin ? configuredMin : NaN,
      configuredMax: hasConfiguredMax ? configuredMax : NaN,
    });

    return [{
      ...measure,
      data,
      ia: `${analysis}\n${technicalAnalysis}`,
      technicalAnalysis,
      stats,
      type,
      unit,
      severity,
      configuredLimits: {
        min: hasConfiguredMin ? configuredMin : null,
        max: hasConfiguredMax ? configuredMax : null,
      },
      limitSource: relatedAlarms.length ? "Alarma configurada" : (hasConfiguredMin || hasConfiguredMax ? "Rango de medición" : "Sin límites"),
      relatedAlarmIds: relatedAlarms.map((alarm) => alarm?.id).filter(Boolean),
      distribution: (hasConfiguredMin || hasConfiguredMax) ? [
        { key: "below", label: "Bajo límite", value: belowCount, percentage: numericValues.length ? belowCount / numericValues.length * 100 : 0, color: "#3B82F6" },
        { key: "within", label: "En rango", value: withinCount, percentage: numericValues.length ? withinCount / numericValues.length * 100 : 0, color: "#24A148" },
        { key: "above", label: "Sobre límite", value: aboveCount, percentage: numericValues.length ? aboveCount / numericValues.length * 100 : 0, color: "#E5484D" },
      ].filter((item) => item.key === "within" || (item.key === "below" && hasConfiguredMin) || (item.key === "above" && hasConfiguredMax)) : null,
    }];
  });

  const noDataMessage = "No hay mediciones disponibles para generar el reporte inteligente.";
  const totalSamples = measures.reduce((sum, measure) => sum + measure.stats.count, 0);
  const alerts = measures.reduce((sum, measure) => sum + measure.stats.outOfRangeCount + measure.stats.anomalyCount, 0);
  const phaseInsights = [
    getPhaseInsight(measures, "voltage", "tensión"),
    getPhaseInsight(measures, "current", "corriente"),
  ].filter(Boolean);
  const alertConfigurations = (Array.isArray(alarms) ? alarms : []).map((alarm) => buildAlertConfiguration(alarm, measures));
  const configuredEvents = (Array.isArray(eventConfigurations) ? eventConfigurations : []).map(buildEventConfiguration);
  const recommendations = [];
  if (measures.some((measure) => measure.stats.outOfRangeCount > 0)) {
    recommendations.push("Revisar los registros fuera de los límites configurados y correlacionarlos con alarmas, carga y operación del equipo.");
  }
  if (measures.some((measure) => measure.stats.anomalyCount > 0)) {
    recommendations.push("Validar los valores atípicos contra eventos operativos y descartar errores de comunicación o sensado.");
  }
  if (phaseInsights.some((insight) => insight.severity === "warning")) {
    recommendations.push("Revisar distribución de cargas y conexiones entre fases; confirmar el desbalance con instrumentación adecuada.");
  }
  if (measures.some((measure) => measure.configuredLimits.min === null && measure.configuredLimits.max === null)) {
    recommendations.push("Configurar valores nominales y límites operativos por tipo de medición para habilitar una evaluación de cumplimiento más precisa.");
  }
  if (!recommendations.length && measures.length) {
    recommendations.push("Mantener el monitoreo y comparar los siguientes periodos para confirmar la estabilidad observada.");
  }

  const executiveSummary = measures.length
    ? `Se analizaron ${totalSamples} registros correspondientes a ${measures.length} ${measures.length === 1 ? "variable" : "variables"}. ` +
      `${measures.filter((measure) => measure.severity === "ok").length} series mostraron comportamiento estadísticamente estable y se detectaron ${alerts} observaciones que requieren contexto o revisión.`
    : noDataMessage;
  const fullText = measures.length
    ? `${header}${executiveSummary}\n\n${measures.map((measure) => measure.ia).join("\n\n")}\n\nRecomendaciones:\n${recommendations.map((item) => `- ${item}`).join("\n")}`
    : `${header}${noDataMessage}`;

  return {
    header,
    ia: fullText,
    measures,
    hasData: measures.length > 0,
    noDataMessage,
    executiveSummary,
    phaseInsights,
    recommendations,
    overview: {
      measureCount: measures.length,
      totalSamples,
      alerts,
      stableMeasures: measures.filter((measure) => measure.severity === "ok").length,
      configuredAlerts: alertConfigurations.length,
      configuredEvents: configuredEvents.length,
    },
    alertConfigurations,
    eventConfigurations: configuredEvents,
    device: {
      type: getDeviceType(selected),
      location: getDeviceLocation(selected),
      name: selected?.vc_nombre || "No identificado",
      id: selected?.id_dispositivo || selected?.id || "No identificado",
    },
    metadata: {
      generatedAt: generatedAt.toISOString(),
      generatedDate: generatedAt.locale("es-MX").format("DD MMMM YYYY, HH:mm"),
      supervisor: [user?.vc_nombre, user?.vc_paterno, user?.vc_materno].filter(Boolean).join(" ") || "No identificado",
      deviceName: selected?.vc_nombre || "No identificado",
      deviceId: selected?.id_dispositivo || selected?.id || "No identificado",
      periodStart: range?.[0] ? moment(range[0]).locale("es-MX").format("DD MMM YYYY") : "Inicio de datos",
      periodEnd: range?.[1] ? moment(range[1]).locale("es-MX").format("DD MMM YYYY") : "Fin de datos",
    },
    disclaimer: "Análisis automático orientativo basado en los datos disponibles. No sustituye una inspección eléctrica, un estudio de calidad de energía ni la evaluación de cumplimiento por personal calificado.",
  };
};
